import { useMemo } from 'react';
import styled from '@emotion/styled';
import { Remirror as RemirrorTypes } from '@trinity/chat';
import { CollapserCompact, SeeMore } from '@trinity/chat-components/components/presentation/SeeMore';
import { useRemirrorEditor } from '@trinity/chat-components/components/Remirror/BasicMarkupEditor';
import { VideoPlayer } from '@trinity/chat-components/components/VideoPlayer';
import { MiniAudioPlayer } from '../AudioPlayer/MiniAudioPlayer';


type Props = {
  quote: RemirrorTypes.RootNode;
  insights: RemirrorTypes.RootNode;
  media: {
    clipUrl?: string;
    type: 'video' | 'audio' | null;
    url: string | null;
  };
};

export const WidgetQuoteCardCompact = (props: Props) => {
  const { state: quote } = useRemirrorEditor({ content: props.quote });
  const { state: insights } = useRemirrorEditor({ content: props.insights });

  const url = useMemo(() => {
    return props.media?.clipUrl || props.media?.url || null;
  }, [props.media]);

  const ready = useMemo(() => {
    return {
      audio: props.media?.type === 'audio' && !!url,
      video: props.media?.type === 'video' && !!url,
      empty: !props.media?.type || !url,
    };
  }, [props.media, url]);

  return (
    <Wrap>
      <Main>
        <Text>
          {!isContentEmpty(props.insights) &&
            <Insights>
              {insights.doc.textContent}
            </Insights>
          }

          {ready.empty &&
            <NoMediaQuote>
              <SeeMore
                css={{ bottom: '-10px', right: '-10px' }}
                CollapserComp={CollapserCompact}
                lineHeight={15}
                maxLines={5}>
                {`"${quote.doc.textContent}"`}
              </SeeMore>
            </NoMediaQuote>
          }

          {ready.audio &&
            <Media>
              <MiniAudioPlayer audio={{ src: url }} />
              <Snippet>
                <SeeMore
                  css={{ bottom: '-10px' }}
                  CollapserComp={CollapserCompact}
                  lineHeight={15}
                  maxLines={6}>
                  {`"${quote.doc.textContent}"`}
                </SeeMore>
              </Snippet>
            </Media>
          }

          {ready.video &&
            <VideoBox>
              <Video>
                <VideoPlayer
                  autoplay={false}
                  controls
                  style={{ maxHeight: '90px', width: `calc(${90 * (16/9)}px)` }}
                  src={url} />
              </Video>
              <VideoText>
                <SeeMore
                  css={{ bottom: '-10px', right: '-10px' }}
                  CollapserComp={CollapserCompact}
                  lineHeight={15}
                  maxLines={5}>
                  {`"${quote.doc.textContent}"`}
                </SeeMore>
              </VideoText>
            </VideoBox>
          }
        </Text>
      </Main>
    </Wrap>
  );
};

WidgetQuoteCardCompact.displayName = 'Widget.Quote.Card.Compact';

const Wrap = styled.div`
  padding: 5px 10px;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
`;

const Text = styled.div`
  font-size: 15px;
`;

const NoMediaQuote = styled.div`
  padding: 10px;
  border-radius: 8px;
  border: 1px solid var(--sec-01);
  box-shadow: 0px 1px 5px rgba(0,0,0,0.12);
  font-size: 13px;
  font-style: italic;
`;

const VideoText = styled.div`
  margin: 5px;
  font-size: 13px;
  font-style: italic;
`;

const Media = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 10px 5px;
  border-radius: 8px;
  border: 1px solid var(--sec-01);
  box-shadow: 0px 1px 5px rgba(0,0,0,0.12);
  margin-top: 10px;
`;

const Snippet = styled.div`
  font-size: 13px;
  font-style: italic;
`;

const Video = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: 90px;
  padding-top: 10px;
`;

const VideoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 10px 10px;
  border-radius: 8px;
  border: 1px solid var(--sec-01);
  box-shadow: 0px 1px 5px rgba(0,0,0,0.12);
`;

const Insights = styled.div`
  margin-bottom: 10px;
`;

function isContentEmpty(value: RemirrorTypes.RootNode) {

  if (!value) return true;

  function isParagraphNodeEmpty(node: RemirrorTypes.ParagraphNode) {
    const content = (node.content || []);
    if (!content.length) return true;

    return content.every(isParagraphContentEmpty);
  }

  return (value.content || []).reduce((acc, node) => {
    if (!acc) return acc;

    return isParagraphNodeEmpty(node);
  }, true);
}

function isParagraphContentEmpty(node: RemirrorTypes.TextNode) {

  if (node.type === RemirrorTypes.NodeType.Text) {
    return !node.text.trim().length;
  }

  return false;
}